// External dependencies
// import { nanoid } from "nanoid";

import { isEmpty } from "@swiggy-private/js-utils";
import * as WalletApi from "@payment-client/interfaces/WalletApi";

// Internal utilities
import { makeDownstreamApiCall } from "@swiggy-private/checkout-core";

// Types and interfaces
import {
    CheckBalanceApiResponse,
    LinkWalletParams,
    LinkableWallet,
    AddMoneyToWalletParams,
    AddMoneyWalletCode,
    WalletFormData,
    MobikwikCheckSumData,
    AddMoneyResponseData,
    PaytmSSOData,
    WalletLinkExtraParams,
} from "@payment-client/interfaces/WalletPublic";

// Wallet utilities
import { getWalletAddMoneyReturnUrl, getWalletHttpOptions, getWalletKey } from "@payment-client/helpers/WalletUtils";
import { getWalletForm } from "@payment-client/helpers/Wallet";
import { ApiResponse } from "@payment-client/interfaces/Api";
import { HTTP_METHODS } from "@swiggy-private/checkout-core";

export async function checkWalletBalance(
    apiHost: string,
    walletCode: string,
): Promise<ApiResponse<CheckBalanceApiResponse>> {
    const walletOptions = getWalletHttpOptions(walletCode, "checkBalance");
    if (walletOptions.isErr()) {
        throw new Error(walletOptions.unwrapErr());
    }

    const { endpoint } = walletOptions.unwrap();
    const response = (await makeDownstreamApiCall({
        apiHost,
        endpoint,
    })) as ApiResponse<CheckBalanceApiResponse>;

    return response;
}

export async function linkWallet(
    apiHost: string,
    walletCode: string,
    mobileNumber?: string,
    params?: WalletLinkExtraParams,
): Promise<ApiResponse<unknown>> {
    const walletOptions = getWalletHttpOptions(walletCode, "link", HTTP_METHODS.POST);
    if (walletOptions.isErr()) {
        throw new Error(walletOptions.unwrapErr());
    }

    const { endpoint, method } = walletOptions.unwrap();
    let bodyParams: { [key: string]: string } = {};
    if (!isEmpty(mobileNumber)) {
        bodyParams.mobileNumber = mobileNumber;
    }

    if (!isEmpty(params)) {
        bodyParams = { ...bodyParams, ...params };
    }

    const response = (await makeDownstreamApiCall({
        apiHost,
        endpoint,
        bodyParams,
        options: { method },
    })) as ApiResponse<object>;

    return response;
}

export async function validateLinkWallet(
    apiHost: string,
    { walletCode, ...params }: LinkWalletParams,
): Promise<ApiResponse<WalletApi.VerifyOtpResponse>> {
    const { otp, otpId, state } = params;

    // Filter out undefined values for searchParams, similar to api-server's toQueryParams function
    const searchParams: { [key: string]: string } = {};
    if (otp) searchParams.otp = otp;
    if (state) searchParams.state = state;
    if (otpId) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        searchParams.otp_id = otpId;
    }

    const walletOptions = getWalletHttpOptions(walletCode, "validateOtp", HTTP_METHODS.POST);
    if (walletOptions.isErr()) {
        throw new Error(walletOptions.unwrapErr());
    }

    const opts = walletOptions.unwrap();
    const response = (await makeDownstreamApiCall({
        apiHost,
        endpoint: opts.endpoint,
        bodyParams: LinkableWallet.PHONEPE === walletCode ? (params as unknown as { [key: string]: unknown }) : {},
        searchParams,
        options: { method: opts.method },
    })) as ApiResponse<WalletApi.VerifyOtpResponse>;

    return response;
}

export async function delinkWallet(apiHost: string, walletCode: string): Promise<ApiResponse<AddMoneyResponseData>> {
    const walletOptions = getWalletHttpOptions(walletCode, "delink", "POST");
    if (walletOptions.isErr()) {
        throw new Error(walletOptions.unwrapErr());
    }

    const { endpoint, method } = walletOptions.unwrap();
    const response = (await makeDownstreamApiCall({
        apiHost,
        endpoint,
        bodyParams: { walletCode },
        options: { method },
    })) as ApiResponse<AddMoneyResponseData>;

    return response;
}

export async function addMoneyToWallet(
    apiHost: string,
    params: AddMoneyToWalletParams,
): Promise<ApiResponse<AddMoneyResponseData>> {
    const { nanoid } = await import("nanoid");
    const walletCode = params.walletCode.toLowerCase();
    const walletKeyResult = getWalletKey(walletCode);

    if (walletKeyResult.isNone()) {
        throw new Error(`Wallet key not found for ${walletCode}`);
    }

    const { key, method } = walletKeyResult.unwrap();

    const addMoneyReturnUrl = getWalletAddMoneyReturnUrl(walletCode as AddMoneyWalletCode, params);

    const checksumBody = {
        amount: params.amount,
        orderid: nanoid(),
        redirectUrl: addMoneyReturnUrl,
    };

    const bodyParams = walletCode === LinkableWallet.MOBIKWIK ? checksumBody : undefined;

    const walletOptions = getWalletHttpOptions(walletCode, key, method);
    if (walletOptions.isErr()) {
        throw new Error(walletOptions.unwrapErr());
    }

    const opts = walletOptions.unwrap();
    const apiResponse = (await makeDownstreamApiCall({
        apiHost,
        endpoint: opts.endpoint,
        bodyParams: bodyParams,
        options: { method: opts.method },
    })) as ApiResponse<unknown>;

    if (!apiResponse.data) {
        throw new Error(apiResponse.statusMessage || "Oops something went wrong!!");
    }

    const walletFormParams: WalletFormData =
        walletCode === LinkableWallet.MOBIKWIK
            ? ({
                  ...(apiResponse.data as { token: string; checksum: string }),
                  ...checksumBody,
              } as MobikwikCheckSumData)
            : (apiResponse.data as PaytmSSOData);

    const formData = await getWalletForm(walletFormParams, { ...params, addMoneyReturnUrl });

    const result = {
        statusCode: 0,
        data: formData,
    };

    return result;
}
